import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, IconButton, Button, Tooltip, CircularProgress, Alert, Menu, MenuItem, Select,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import type { Api, TenantView, DocSpaceAdmin, DocTreeAdminNode } from './api';
import { DocEditor } from './DocEditor';
import { useImagePicker } from './MediaPicker';
import { PromptDialog, ConfirmDialog } from './dialogs';

interface TextAsk { title: string; label?: string; initial?: string; confirmLabel?: string; resolve: (v: string | null) => void }
interface ConfirmAsk { title: string; message: string; confirmLabel?: string; danger?: boolean; resolve: (ok: boolean) => void }

const DOT: Record<string, string> = { published: '#16a34a', draft: '#94a3b8', ahead: '#d97706' };

export function DocsManager({
  api, tenant, canEdit, canPublish, jumpTo, onJumped,
}: { api: Api; tenant: TenantView; canEdit: boolean; canPublish: boolean; jumpTo?: { pageId: string; spaceId: string } | null; onJumped?: () => void }) {
  const [versions, setVersions] = useState<DocSpaceAdmin[] | null>(null);
  const [spaceId, setSpaceId] = useState<string>('');
  const [tree, setTree] = useState<DocTreeAdminNode[] | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [err, setErr] = useState('');
  const [importing, setImporting] = useState(false);
  const [menu, setMenu] = useState<{ el: HTMLElement; node: DocTreeAdminNode } | null>(null);
  const [verMenu, setVerMenu] = useState<null | HTMLElement>(null);
  const [textAsk, setTextAsk] = useState<TextAsk | null>(null);
  const [confirmAsk, setConfirmAsk] = useState<ConfirmAsk | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const picker = useImagePicker();

  const askText = (o: Omit<TextAsk, 'resolve'>) => new Promise<string | null>((resolve) => setTextAsk({ ...o, resolve }));
  const askConfirm = (o: Omit<ConfirmAsk, 'resolve'>) => new Promise<boolean>((resolve) => setConfirmAsk({ ...o, resolve }));

  const space = versions?.find((v) => v.id === spaceId) ?? null;

  const loadVersions = () => api.docVersions(tenant.tenantId).then((vs) => {
    setVersions(vs);
    setSpaceId((cur) => (vs.some((v) => v.id === cur) ? cur : (vs.find((v) => v.isDefault) ?? vs[0])?.id ?? ''));
  }).catch((e) => setErr(e.message));
  useEffect(() => { loadVersions(); }, [tenant.tenantId]);

  // When a jump asks for a page in a not-yet-loaded space, remember which page
  // to select once that space's tree arrives.
  const pendingSel = useRef<string | null>(null);
  const loadTree = (id: string) => api.docTree(tenant.tenantId, id).then((t) => {
    setTree(t);
    if (pendingSel.current) {
      selectAndReveal(t, pendingSel.current);
      pendingSel.current = null;
    }
  }).catch((e) => setErr(e.message));

  // Select a page AND expand all its ancestors so it's visible + highlighted.
  const selectAndReveal = (nodes: DocTreeAdminNode[], pageId: string) => {
    const trail: string[] = [];
    const find = (list: DocTreeAdminNode[], acc: string[]): boolean => {
      for (const n of list) {
        if (n.id === pageId) return true;
        if (n.children.length && find(n.children, acc)) { acc.unshift(n.id); return true; }
      }
      return false;
    };
    find(nodes, trail);
    if (trail.length) setExpanded((s) => { const n = new Set(s); trail.forEach((id) => n.add(id)); return n; });
    setSel(pageId);
  };
  useEffect(() => { if (spaceId) { setTree(null); setSel(null); loadTree(spaceId); } }, [spaceId]);
  // Open a specific page (jumped from Doc Reviews) — switch to ITS version first,
  // then select it once that space's tree has loaded.
  useEffect(() => {
    if (!jumpTo) return;
    onJumped?.();
    if (jumpTo.spaceId && jumpTo.spaceId !== spaceId) {
      pendingSel.current = jumpTo.pageId;
      setSpaceId(jumpTo.spaceId); // triggers loadTree, which applies pendingSel
    } else if (tree) {
      selectAndReveal(tree, jumpTo.pageId);
    } else {
      pendingSel.current = jumpTo.pageId; // tree still loading for this space
    }
  }, [jumpTo]);

  const refresh = () => { if (spaceId) loadTree(spaceId); };

  const newVersion = async () => {
    if (!space) return;
    const label = await askText({ title: 'New version', label: 'Version label (e.g. 3.0)', confirmLabel: 'Create & clone' });
    if (!label?.trim()) return;
    try {
      const { id } = await api.createDocVersion(tenant.tenantId, { slug: space.slug, fromVersion: space.version, newVersion: label.trim() });
      await loadVersions(); setSpaceId(id);
    } catch (e: any) { setErr(e.message === 'version_exists' ? 'That version already exists.' : `Couldn’t create version: ${e.message}`); }
  };
  const makeDefault = async () => { if (!space) return; try { await api.setDefaultVersion(tenant.tenantId, space.id); await loadVersions(); } catch (e: any) { setErr(e.message === 'not_published' ? 'Publish the version before making it default.' : e.message); } };
  const setStatus = async (status: string) => { if (!space) return; try { await api.setVersionStatus(tenant.tenantId, space.id, status); await loadVersions(); } catch (e: any) { setErr(e.message === 'default_must_stay_published' ? 'The default version must stay published.' : e.message); } };
  const changeLogo = async () => {
    if (!space || !picker) return;
    const url = await picker.pick(); // browse/upload the media library → /media URL
    if (url === null) return;
    try { await api.setDocSpaceLogo(tenant.tenantId, space.id, url); await loadVersions(); } catch (e: any) { setErr(`Couldn’t set logo: ${e.message}`); }
  };
  const removeLogo = async () => { if (!space) return; try { await api.setDocSpaceLogo(tenant.tenantId, space.id, null); await loadVersions(); } catch (e: any) { setErr(e.message); } };

  const addPage = async (parentId: string | null) => {
    if (!space) return;
    const title = await askText({ title: parentId ? 'New sub-page' : 'New page', label: 'Page title', confirmLabel: 'Create' });
    if (!title?.trim()) return;
    try {
      const { id } = await api.createDocPage(tenant.tenantId, space.id, { title: title.trim(), parentId });
      if (parentId) setExpanded((s) => new Set(s).add(parentId));
      await refresh(); setSel(id);
    } catch (e: any) { setErr(e.message); }
  };
  const rename = async (node: DocTreeAdminNode) => {
    const title = await askText({ title: 'Rename page', label: 'Page title', initial: node.title, confirmLabel: 'Rename' });
    if (!title?.trim() || title.trim() === node.title) return;
    try { await api.renameDocPage(tenant.tenantId, node.id, title.trim()); await refresh(); }
    catch (e: any) { setErr(e.message); }
  };
  const del = async (node: DocTreeAdminNode) => {
    const ok = await askConfirm({ title: 'Delete page', message: `Delete “${node.title}”? This can’t be undone.`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try { await api.deleteDocPage(tenant.tenantId, node.id); if (sel === node.id) setSel(null); await refresh(); }
    catch (e: any) { setErr(e.message === 'has_children' ? 'Delete or move its sub-pages first.' : e.message); }
  };
  const move = async (node: DocTreeAdminNode, dir: 'up' | 'down') => {
    try { await api.moveDocPage(tenant.tenantId, node.id, dir); await refresh(); }
    catch (e: any) { if (e.message !== 'at_edge') setErr(e.message); }
  };

  const importDocx = async (files: FileList | null) => {
    if (!files?.length || !space) return;
    setImporting(true);
    try {
      let firstId: string | null = null;
      for (const f of Array.from(files)) {
        const { id } = await api.importDocx(tenant.tenantId, space.id, f);
        firstId = firstId ?? id;
      }
      await refresh();
      if (firstId) setSel(firstId);
    } catch (e: any) {
      setErr(e.message === 'not_a_docx' ? 'Please choose a .docx file.' : `Import failed: ${e.message}`);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const toggle = (id: string) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const renderNode = (node: DocTreeAdminNode, depth: number): React.ReactNode => {
    const open = expanded.has(node.id);
    const hasKids = node.children.length > 0;
    const dot = !node.published ? DOT.draft : node.draftAhead ? DOT.ahead : DOT.published;
    return (
      <Box key={node.id}>
        <Box
          onClick={() => setSel(node.id)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, pr: 0.5, pl: depth * 1.5 + 0.5, py: 0.5, borderRadius: 1,
            cursor: 'pointer', bgcolor: sel === node.id ? 'action.selected' : undefined,
            '&:hover': { bgcolor: 'action.hover' }, '&:hover .row-actions': { opacity: 1 },
          }}
        >
          <IconButton size="small" sx={{ p: 0.25, visibility: hasKids ? 'visible' : 'hidden' }} onClick={(e) => { e.stopPropagation(); toggle(node.id); }}>
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />
          <Typography variant="body2" noWrap sx={{ flexGrow: 1, fontWeight: sel === node.id ? 600 : 400 }}>{node.title}</Typography>
          {node.protected ? <Tooltip title="Protected"><LockOutlinedIcon sx={{ fontSize: 13, color: 'warning.main', flexShrink: 0 }} /></Tooltip> : null}
          {canEdit ? (
            <Box className="row-actions" sx={{ opacity: 0, transition: 'opacity .1s', display: 'flex' }}>
              <Tooltip title="Add sub-page"><IconButton size="small" onClick={(e) => { e.stopPropagation(); addPage(node.id); }}><AddIcon fontSize="inherit" /></IconButton></Tooltip>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenu({ el: e.currentTarget, node }); }}><MoreVertIcon fontSize="inherit" /></IconButton>
            </Box>
          ) : null}
        </Box>
        {open && hasKids ? node.children.map((c) => renderNode(c, depth + 1)) : null}
      </Box>
    );
  };

  if (err) return <Alert severity="error" onClose={() => setErr('')}>{err}</Alert>;
  if (!versions) return <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!space) return <Alert severity="info">No documentation space yet.</Alert>;

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', height: 'calc(100vh - 108px)' }}>
      <Paper variant="outlined" sx={{ width: 320, flexShrink: 0, height: '100%', overflowY: 'auto', p: 1 }}>
        <Box sx={{ px: 1, py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Select
              size="small" value={spaceId} onChange={(e) => setSpaceId(e.target.value)}
              sx={{ flexGrow: 1, fontSize: 13, '& .MuiSelect-select': { py: 0.6 } }}
            >
              {versions.map((v) => (
                <MenuItem key={v.id} value={v.id} sx={{ fontSize: 13 }}>
                  {v.version}{v.isDefault ? ' · default' : ''}{v.status !== 'PUBLISHED' ? ` · ${v.status.toLowerCase()}` : ''}
                </MenuItem>
              ))}
            </Select>
            {canPublish ? (
              <Tooltip title="Version actions"><IconButton size="small" onClick={(e) => setVerMenu(e.currentTarget)}><MoreVertIcon fontSize="small" /></IconButton></Tooltip>
            ) : null}
          </Box>
          {canPublish ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, px: 0.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>Logo</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 24, border: '1px dashed', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.default' }}>
                {space.logoUrl
                  ? <Box component="img" src={space.logoUrl} alt="logo" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <ImageOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />}
              </Box>
              <Button size="small" sx={{ minWidth: 0, fontSize: 12 }} onClick={changeLogo}>{space.logoUrl ? 'Change' : 'Set'}</Button>
              {space.logoUrl ? <Button size="small" color="inherit" sx={{ minWidth: 0, fontSize: 12 }} onClick={removeLogo}>Remove</Button> : null}
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="overline" color="text.secondary">Pages</Typography>
            {canEdit ? (
              <Box>
                <input ref={fileRef} type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" multiple hidden onChange={(e) => importDocx(e.target.files)} />
                <Tooltip title="Import Word (.docx)"><span><IconButton size="small" disabled={importing} onClick={() => fileRef.current?.click()}>{importing ? <CircularProgress size={16} /> : <UploadFileIcon fontSize="small" />}</IconButton></span></Tooltip>
                <Button size="small" startIcon={<AddIcon />} onClick={() => addPage(null)}>New</Button>
              </Box>
            ) : null}
          </Box>
        </Box>
        {!tree ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={22} /></Box>
          : tree.map((n) => renderNode(n, 0))}
      </Paper>

      <Box sx={{ flexGrow: 1, minWidth: 0, height: '100%', overflowY: 'auto', pt: 1.5, px: 0.5 }}>
        {sel ? (
          <DocEditor key={sel} api={api} tenant={tenant} pageId={sel} canPublish={canPublish} onSaved={refresh} />
        ) : (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>Select a page to edit{canEdit ? ', or create one with “New”.' : '.'}</Typography>
          </Paper>
        )}
      </Box>

      <Menu open={!!verMenu} anchorEl={verMenu} onClose={() => setVerMenu(null)}>
        <MenuItem onClick={() => { newVersion(); setVerMenu(null); }}>New version (clone current)</MenuItem>
        {space.status !== 'PUBLISHED' ? <MenuItem onClick={() => { setStatus('PUBLISHED'); setVerMenu(null); }}>Publish version</MenuItem> : null}
        {space.status === 'PUBLISHED' && !space.isDefault ? <MenuItem onClick={() => { makeDefault(); setVerMenu(null); }}>Set as default</MenuItem> : null}
        {space.status === 'PUBLISHED' && !space.isDefault ? <MenuItem onClick={() => { setStatus('ARCHIVED'); setVerMenu(null); }}>Archive version</MenuItem> : null}
      </Menu>

      <Menu open={!!menu} anchorEl={menu?.el} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { if (menu) rename(menu.node); setMenu(null); }}>Rename</MenuItem>
        <MenuItem onClick={() => { if (menu) move(menu.node, 'up'); setMenu(null); }}>Move up</MenuItem>
        <MenuItem onClick={() => { if (menu) move(menu.node, 'down'); setMenu(null); }}>Move down</MenuItem>
        <MenuItem onClick={() => { if (menu) addPage(menu.node.id); setMenu(null); }}>Add sub-page</MenuItem>
        <MenuItem sx={{ color: 'error.main' }} onClick={() => { if (menu) del(menu.node); setMenu(null); }}>Delete</MenuItem>
      </Menu>

      <PromptDialog
        open={!!textAsk} title={textAsk?.title ?? ''} label={textAsk?.label} initial={textAsk?.initial ?? ''}
        confirmLabel={textAsk?.confirmLabel}
        onClose={(v) => { textAsk?.resolve(v); setTextAsk(null); }}
      />
      <ConfirmDialog
        open={!!confirmAsk} title={confirmAsk?.title ?? ''} message={confirmAsk?.message ?? ''}
        confirmLabel={confirmAsk?.confirmLabel} danger={confirmAsk?.danger}
        onClose={(ok) => { confirmAsk?.resolve(ok); setConfirmAsk(null); }}
      />
    </Box>
  );
}
